import React, { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../utils/config";
import {
  FiBriefcase, FiSearch, FiX, FiMapPin, FiClock, FiEye, FiInfo, FiHeart, FiRefreshCw, FiClipboard
} from "react-icons/fi";
import "./EmployeeDashboard.css";

const Jobs = () => {
  const { allJobs, searchQuery: globalSearchQuery, savedJobs = [], toggleSaveJob } = useOutletContext();
  const navigate = useNavigate();

  // Simple fuzzy matching allowing up to one character difference
  const fuzzyMatch = (text, query) => {
    const t = (text || "").toString().toLowerCase();
    const q = query.toLowerCase();
    if (t.includes(q)) return true;
    if (Math.abs(t.length - q.length) > 1) return false;
    let mismatches = 0;
    const minLen = Math.min(t.length, q.length);
    for (let i = 0; i < minLen; i++) {
      if (t[i] !== q[i]) mismatches++;
      if (mismatches > 1) return false;
    }
    return true;
  };

  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [salaryFilter, setSalaryFilter] = useState("");
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [salaries, setSalaries] = useState([]);

  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [isDeptDropdownOpen, setIsDeptDropdownOpen] = useState(false);
  const [deptSearchQuery, setDeptSearchQuery] = useState("");
  const [isSalaryDropdownOpen, setIsSalaryDropdownOpen] = useState(false);
  const [salarySearchQuery, setSalarySearchQuery] = useState("");

  const roleDropdownRef = React.useRef(null);
  const deptDropdownRef = React.useRef(null);
  const salaryDropdownRef = React.useRef(null);

  useEffect(() => {
    // Extract filters from only short jobs
    const shortJobs = allJobs.filter(job => job.jobType === "short");

    // Extract unique departments
    const depts = Array.from(new Set(shortJobs.map(job => job.department).filter(Boolean))).sort();
    setDepartments(depts);

    // Extract unique roles
    const roleNames = Array.from(new Set(shortJobs.map(job => job.role).filter(Boolean))).sort();
    setRoles(roleNames);

    // Extract unique salaries
    const salaryValues = Array.from(new Set(shortJobs.map(job => job.salary).filter(Boolean))).sort();
    setSalaries(salaryValues);

    const handleClickOutside = (event) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setIsRoleDropdownOpen(false);
      }
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(event.target)) {
        setIsDeptDropdownOpen(false);
      }
      if (salaryDropdownRef.current && !salaryDropdownRef.current.contains(event.target)) {
        setIsSalaryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [allJobs]);

  const filteredJobs = allJobs.filter(job => {
    // Only show short jobs
    if (job.jobType !== "short") return false;

    const combinedQuery = (globalSearchQuery || localSearchQuery).toLowerCase();
    const matchesSearch =
+      fuzzyMatch(job.role, combinedQuery) ||
+      fuzzyMatch(job.location, combinedQuery) ||
+      fuzzyMatch(job.department || "", combinedQuery);


    const matchesRole = roleFilter ? job.role === roleFilter : true;
    const matchesDept = departmentFilter ? job.department === departmentFilter : true;
    const matchesSalary = salaryFilter ? job.salary === salaryFilter : true;

    return matchesSearch && matchesRole && matchesDept && matchesSalary;
  });

  const resetFilters = () => {
    setLocalSearchQuery("");
    setRoleFilter("");
    setDepartmentFilter("");
    setSalaryFilter("");
    setRoleSearchQuery("");
    setDeptSearchQuery("");
    setSalarySearchQuery("");
  };

  return (
    <div className="emp-dash">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Dashboard Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Short <span>Jobs</span>
            </h1>
            <p className="emp-dash__subtitle">
              Quick gigs and short-term roles
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiBriefcase />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* Filter Section */}
        <div className="emp-dash__card mb-6">
          <div className="emp-dash__card-header">
            <div>
              <h3 className="emp-dash__card-title flex items-center gap-2">
                <FiSearch className="text-orange-600" /> Filter Short Jobs
              </h3>
              <p className="emp-dash__card-desc">Search by role, location, or department</p>
            </div>
          </div>
          <div className="emp-dash__card-body bg-gray-50/50">
            {/* Search Bar */}
            <div className="relative w-full">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input
                type="text"
                placeholder="Search short jobs..."
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 text-sm border border-gray-200 rounded-2xl focus:ring-2 focus:ring-orange-500 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-300 bg-white"
              />
            </div>
          </div>
        </div>

        {/* Filter Buttons Row */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* Role Filter */}
          <div className="relative" ref={roleDropdownRef}>
            <button
              onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
              className={`h-11 px-5 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 shadow-md ${
                roleFilter 
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 shadow-orange-200' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-orange-300'
              }`}
            >
              <FiBriefcase className="text-sm" /> Role {roleFilter && `: ${roleFilter}`}
            </button>

                {/* Role Filter Dropdown */}
                {isRoleDropdownOpen && (
                  <div className="absolute left-0 z-50 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-xl max-h-80 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                    <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-amber-50">
                      <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                          type="text"
                          className="w-full py-2 pl-9 pr-3 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="Search roles..."
                          value={roleSearchQuery}
                          onChange={(e) => setRoleSearchQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="p-1">
                      <div
                        onClick={() => { setRoleFilter(''); setIsRoleDropdownOpen(false); setRoleSearchQuery(''); }}
                        className={`px-4 py-3 text-sm hover:bg-orange-50 cursor-pointer rounded-lg transition-all duration-150 font-medium ${!roleFilter ? 'bg-orange-100 text-orange-700' : 'text-gray-700 hover:text-gray-900'
                          }`}
                      >
                        All Roles
                      </div>
                      {roles
                        .filter(r => r.toLowerCase().includes(roleSearchQuery.toLowerCase()))
                        .map((r, i) => (
                          <div
                            key={i}
                            onClick={() => { setRoleFilter(r); setIsRoleDropdownOpen(false); setRoleSearchQuery(''); }}
                            className={`px-4 py-3 text-sm hover:bg-orange-50 cursor-pointer rounded-lg transition-all duration-150 font-medium ${roleFilter === r ? 'bg-orange-100 text-orange-700' : 'text-gray-700 hover:text-gray-900'
                          }`}
                          >
                            {r}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
          </div>

          {/* Department Filter */}
          <div className="relative" ref={deptDropdownRef}>
            <button
              onClick={() => setIsDeptDropdownOpen(!isDeptDropdownOpen)}
              className={`h-11 px-5 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 shadow-md ${
                departmentFilter 
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700 shadow-purple-200' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-purple-300'
              }`}
            >
              <FiBriefcase className="text-sm" /> Dept {departmentFilter && `: ${departmentFilter}`}
            </button>

            {/* Department Filter Dropdown */}
            {isDeptDropdownOpen && (
              <div className="absolute left-0 z-50 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-xl max-h-80 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                    <input
                      type="text"
                      className="w-full py-2 pl-9 pr-3 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Search departments..."
                      value={deptSearchQuery}
                      onChange={(e) => setDeptSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="p-1">
                  <div
                    onClick={() => {
                      setDepartmentFilter('');
                      setIsDeptDropdownOpen(false);
                      setDeptSearchQuery('');
                    }}
                    className={`px-4 py-3 text-sm hover:bg-purple-50 cursor-pointer rounded-lg transition-all duration-150 font-medium ${!departmentFilter ? 'bg-purple-100 text-purple-700' : 'text-gray-700 hover:text-gray-900'
                      }`}
                  >
                    All Departments
                  </div>
                  {departments
                    .filter(d => d.toLowerCase().includes(deptSearchQuery.toLowerCase()))
                    .map((d, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          setDepartmentFilter(d);
                          setIsDeptDropdownOpen(false);
                          setDeptSearchQuery('');
                        }}
                        className={`px-4 py-3 text-sm hover:bg-purple-50 cursor-pointer rounded-lg transition-all duration-150 font-medium ${departmentFilter === d ? 'bg-purple-100 text-purple-700' : 'text-gray-700 hover:text-gray-900'
                        }`}
                      >
                        {d}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Salary Filter */}
          <div className="relative" ref={salaryDropdownRef}>
            <button
              onClick={() => setIsSalaryDropdownOpen(!isSalaryDropdownOpen)}
              className={`h-11 px-5 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 shadow-md ${
                salaryFilter 
                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 shadow-emerald-200' 
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-emerald-300'
              }`}
            >
              <FiClipboard className="text-sm" /> Salary {salaryFilter && `: ${salaryFilter}`}
            </button>

            {/* Salary Filter Dropdown */}
            {isSalaryDropdownOpen && (
              <div className="absolute left-0 z-50 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-xl max-h-80 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
                <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                    <input
                      type="text"
                      className="w-full py-2 pl-9 pr-3 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Search salaries..."
                      value={salarySearchQuery}
                      onChange={(e) => setSalarySearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="p-1">
                  <div
                    onClick={() => {
                      setSalaryFilter('');
                      setIsSalaryDropdownOpen(false);
                      setSalarySearchQuery('');
                    }}
                    className={`px-4 py-3 text-sm hover:bg-emerald-50 cursor-pointer rounded-lg transition-all duration-150 font-medium ${!salaryFilter ? 'bg-emerald-100 text-emerald-700' : 'text-gray-700 hover:text-gray-900'
                      }`}
                  >
                    All Salaries
                  </div>
                  {salaries
                    .filter(s => s.toLowerCase().includes(salarySearchQuery.toLowerCase()))
                    .map((sal, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          setSalaryFilter(sal);
                          setIsSalaryDropdownOpen(false);
                          setSalarySearchQuery('');
                        }}
                        className={`px-4 py-3 text-sm hover:bg-emerald-50 cursor-pointer rounded-lg transition-all duration-150 font-medium ${salaryFilter === sal ? 'bg-emerald-100 text-emerald-700' : 'text-gray-700 hover:text-gray-900'
                        }`}
                      >
                        {sal}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Clear Filters Button */}
          {(localSearchQuery || roleFilter || departmentFilter || salaryFilter) && (
            <button
              onClick={resetFilters}
              className="h-11 px-5 text-sm font-semibold text-gray-700 bg-white border-2 border-red-200 rounded-xl hover:bg-red-50 hover:border-red-300 transition-all duration-200 shadow-md flex items-center gap-2"
            >
              <FiRefreshCw className="text-sm" /> Clear Filters
            </button>
          )}
        </div>

        {/* Table Section */}
        <div className="emp-dash__card">
          <div className="emp-dash__card-header">
            <div>
              <h3 className="emp-dash__card-title flex items-center gap-2">
                <FiBriefcase className="text-orange-600" /> Short Job Listings
              </h3>
              <p className="emp-dash__card-desc">Showing {filteredJobs.length} short jobs</p>
            </div>
          </div>
          {filteredJobs.length > 0 ? (
            <div className="emp-dash__table-wrap overflow-x-auto !block">
              <table className="emp-dash__table min-w-[800px]">
                <thead>
                  <tr>
                    <th className="py-3 px-4 text-center font-bold">Job Position</th>
                    <th className="py-3 px-4 text-center font-bold hidden md:table-cell">Duration</th>
                    <th className="py-3 px-4 text-center font-bold hidden md:table-cell">Location</th>
                    <th className="py-3 px-4 text-center font-bold">Experience</th>
                    <th className="py-3 px-4 text-center font-bold hidden md:table-cell">Pay / Day</th>
                    <th className="py-3 px-4 text-center font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.map((job) => (
                    <tr key={job._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-2 text-center">
                        <div className="text-xs font-bold text-gray-800 uppercase tracking-tight">{job.role}</div>
                        <div className="text-[9px] text-orange-600 font-black uppercase tracking-widest mt-0.5">{job.department || "General"}</div>
                      </td>
                      <td className="px-4 py-2 text-center hidden md:table-cell">
                        <div className="flex flex-col items-center gap-1">
                          {(job.startTime || job.endTime) && (
                            <div className="text-[9px] font-black text-gray-500 uppercase tracking-tighter">
                              {job.startTime || "??:??"} - {job.endTime || "??:??"}
                            </div>
                          )}
                          <span className="text-[10px] font-bold text-orange-700 bg-orange-50 px-2.5 py-1 rounded-lg uppercase tracking-wider border border-orange-100 shadow-sm">
                            {job.totalDuration || "Short Term"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center hidden md:table-cell">
                        <div className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                          <FiMapPin className="text-orange-400" size={10} />
                          {job.location}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center align-middle">
                        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider bg-gray-50 px-2.5 py-1 rounded-md border border-gray-100">
                          <FiClock className="text-amber-500" size={11} />
                          {job.experience || "Fresher"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center align-middle hidden md:table-cell">
                        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold text-gray-700 uppercase tracking-wider bg-orange-50 px-2.5 py-1 rounded-md border border-orange-100">
                          <span className="text-orange-600">₹</span>
                          {job.salary || "Negotiable"}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                          <button
                            onClick={() => toggleSaveJob(job._id)}
                            className={`px-3 py-1 text-[9px] font-bold uppercase tracking-widest rounded-lg border transition-all flex items-center gap-1 ${savedJobs.some(sj => (sj._id || sj) === job._id)
                                ? 'bg-red-50 text-red-600 border-red-100 hover:bg-red-100'
                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                              }`}
                          >
                            <FiHeart /> {savedJobs.some(sj => (sj._id || sj) === job._id) ? "Saved" : "Save"}
                          </button>
                          <button
                            onClick={() => navigate(`/jobs/${job._id}`)}
                            className="px-4 py-1.5 bg-orange-600 text-white text-[9px] font-black uppercase tracking-widest rounded-lg hover:bg-orange-700 transition-all flex items-center gap-1.5 shadow-md shadow-orange-100"
                          >
                            <FiEye size={10} /> View & Apply
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-10 text-center">
              <div className="flex flex-col items-center justify-center gap-3">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-300">
                  <FiBriefcase size={32} />
                </div>
                <span className="text-sm font-medium text-gray-500">No short jobs found matching your filters</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest py-4">
          <FiInfo className="text-orange-400" />
          Timely Health Recruitment Tracker • {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
};

export default Jobs;